/*
 * Reset Password — step 2 of password recovery.
 *
 * Reads the identifier from router state (set by ForgotPassword), takes the
 * 6-digit code from the email + new password (twice), POSTs to
 * /auth/reset-password, and on success bounces the user to Login with a
 * success notice in state.
 *
 * If a user lands here directly (no state.identifier), we still let them
 * fill in identifier manually — works for deep-linking from a future email
 * that pre-fills the code.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

import { api } from '@/lib/api';
import { BrandWordmark } from '@/components/BrandWordmark';

interface ResetPasswordState {
  identifier?: string;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = (location.state as ResetPasswordState | null)?.identifier ?? '';

  const [identifier, setIdentifier] = useState(incoming);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordsMatch =
    !confirmPassword || newPassword === confirmPassword;
  const codeValid = code.trim().length === 6 && /^\d+$/.test(code.trim());

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!identifier.trim()) {
      setError('Please enter your username or email.');
      return;
    }
    if (!codeValid) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword(identifier.trim(), code.trim(), newPassword);
      // Success — bounce to Login with a flash message in state.
      navigate('/app/login', {
        replace: true,
        state: { notice: 'Password updated — sign in with your new password.' },
      });
    } catch (err: any) {
      setError(err?.message ?? 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <Link to="/" className="auth-brand">
          <BrandWordmark className="brand-wordmark" />
        </Link>

        <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 12px' }}>
          <Mail width={36} height={36} color="#22c55e" strokeWidth={1.75} />
        </div>

        <h1 className="auth-title">Check your email</h1>
        <p className="auth-sub">
          We sent a 6-digit code to the email on your account. It expires in 15 minutes.
        </p>

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          {/* If we got here from ForgotPassword we hide the identifier (already
              known). If user deep-linked here, show the field so they can fill it. */}
          {!incoming && (
            <label className="auth-label">
              <span>USERNAME OR EMAIL</span>
              <input
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your username or email"
                className="auth-input"
                disabled={loading}
              />
            </label>
          )}

          <label className="auth-label">
            <span>RESET CODE</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="auth-input"
              style={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 22,
                letterSpacing: 6,
                textAlign: 'center',
              }}
              disabled={loading}
              autoFocus={!!incoming}
            />
          </label>

          <label className="auth-label">
            <span>NEW PASSWORD</span>
            <div className="auth-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="auth-input auth-input-pad-right"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="auth-eye-btn"
                tabIndex={-1}
              >
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </label>

          <label className="auth-label">
            <span>CONFIRM PASSWORD</span>
            <div className="auth-input-wrap">
              <input
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="auth-input auth-input-pad-right"
                disabled={loading}
                style={
                  !passwordsMatch
                    ? { borderColor: '#ef4444' }
                    : undefined
                }
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="auth-eye-btn"
                tabIndex={-1}
              >
                {showConfirm ? 'HIDE' : 'SHOW'}
              </button>
            </div>
            {!passwordsMatch && (
              <span style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>
                Passwords don't match
              </span>
            )}
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-cta" disabled={loading}>
            {loading ? 'Resetting…' : 'Reset password →'}
          </button>

          <div className="auth-footer">
            <Link to="/app/forgot-password" className="auth-footer-link">
              ← Didn't get a code? Resend
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
